import { faComment } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowRotateRight,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Link, Module, ModuleBody } from '@wfp/react';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DisplayToggleWrapper } from 'src/components/DisplayWrapper';
import {
  COMMENTS_TARGET_ID,
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import { buildErrorComponents, sendGAEvent, showNotification } from 'src/utils';

interface RefreshDataSourceButtonProps {
  isFetching?: boolean;
  handleRefreshDataSources: () => void;
}

const RefreshDataSourceButton = ({
  isFetching,
  handleRefreshDataSources,
}: RefreshDataSourceButtonProps) => {
  const title = isFetching
    ? 'Refreshing data sources...'
    : 'Refresh data sources';

  return (
    <Button
      onClick={() => {
        sendGAEvent('refresh_data_sources_btn_clicked');
        handleRefreshDataSources();
      }}
      title={title}
      kind="ghost"
      data-test-id="refresh-data-sources-button"
      disabled={isFetching}
      iconReverse
      icon={<FontAwesomeIcon icon={faArrowRotateRight} />}
    >
      {title}
    </Button>
  );
};

export const SectionControls = ({
  error = null,
  dataSourceRefresh,
  handleRefreshDataSources,
}) => {
  const sectionDetailData = useSelector(
    (state: RootState) => state.viewData?.[SECTION_DETAIL_DATA_KEY]?.data,
  );
  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY].data,
  );

  const errorsList: string[] = useMemo(() => {
    const commonErrorComponents = buildErrorComponents(error);
    const refreshDataSetErrorComponents = buildErrorComponents(
      dataSourceRefresh?.error,
    );

    const notifications = [];

    for (const errors of [
      commonErrorComponents,
      refreshDataSetErrorComponents,
    ]) {
      if (errors.length > 0) {
        notifications.push(errors);
      }
    }
    return notifications;
  }, [error, dataSourceRefresh?.error]);

  useEffect(() => {
    if (errorsList.length > 0) {
      showNotification(<>{errorsList.map(error => error)}</>, 'error', {
        autoClose: false,
        closeOnClick: false,
      });
    }
  }, [errorsList]);

  return (
    <Module className="section-controls">
      <ModuleBody>
        <DisplayToggleWrapper
          targetId={COMMENTS_TARGET_ID}
          component={Link}
          title="Comments"
          data-test-id="show-comments-button"
        >
          <Button
            kind="ghost"
            iconReverse
            icon={<FontAwesomeIcon icon={faComment} />}
          >
            Show comments
          </Button>
        </DisplayToggleWrapper>

        <Button
          kind="ghost"
          iconReverse
          icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
          href={sectionDetailData?.guidance_link || undefined}
          target="_blank"
          title="ACR Guidance link"
          data-test-id="ACR Guidance link"
          onClick={() =>
            sendGAEvent('acr_guidance_link_clicked', { wfp_position: 'header' })
          }
        >
          ACR Guidance
        </Button>
        {sectionDetailData?.data_source_refresh_allowed &&
          reportDetailData &&
          reportDetailData?.state !== 'Published' && (
            <RefreshDataSourceButton
              isFetching={dataSourceRefresh?.isFetching}
              handleRefreshDataSources={handleRefreshDataSources}
            />
          )}
      </ModuleBody>
    </Module>
  );
};

export default SectionControls;
